import React from 'react';

// Standard content width for sub-page sections.
export const PageSection = ({ children, className = '' }) => (
  <div className={`mx-auto w-full max-w-[1500px] px-4 mini:px-8 md:px-12 ${className}`}>{children}</div>
);

// Outlined box holding the page title, directly under the breadcrumb.
// `trail` renders the Home >> No / SCAM >> ... path inside the title box. It
// used to sit in the nav bar, which was the wrong place for it -- it describes
// where you are on the page, not where you can go.
// `aside` is a short description set beside the title at md and up, and under
// it on phones (Bad Dream, 10-1 round).
export const PageTitleBox = ({ children, trail, aside }) => (
  <PageSection className="pt-6">
    <div className="border border-black/25 px-6 py-6 md:px-10 md:py-8">
      {trail && (
        <p className="mb-3 text-[13px] text-black md:text-[15px]">{trail}</p>
      )}
      <div className={aside ? 'md:flex md:items-center md:gap-14' : undefined}>
        <h1 className="text-[26px] font-bold leading-tight text-black md:flex-shrink-0 md:text-[40px]">
          {children}
        </h1>
        {aside && (
          <p className="mt-3 max-w-[58ch] text-[15px] font-light leading-snug text-black md:mt-0 md:text-[17px]">
            {aside}
          </p>
        )}
      </div>
    </div>
  </PageSection>
);

/** Full-bleed coloured title banner used by the company pages. */
export const PageTitleBanner = ({ children, background = '#3A6098', color = '#CFE0F5' }) => (
  <div className="w-full" style={{ backgroundColor: background }}>
    <div className="mx-auto w-full max-w-[1500px] px-4 py-6 mini:px-8 md:px-12 md:py-8">
      <h1 className="text-[28px] font-bold leading-tight md:text-[40px]" style={{ color }}>
        {children}
      </h1>
    </div>
  </div>
);

/** Title over a photograph, as on Resources and Philanthropy Spotlight. */
// `focus` sets which part of the art survives the crop. Phone widths cut a ~4:1
// banner down to about 2:1, so a subject sitting at one edge needs naming or it
// is simply cropped out.
export const PageTitleImage = ({ children, src, alt, color = '#FFFFFF', focus = '' }) => (
  <PageSection className="pt-4">
    <div className="relative">
      <img
        src={src}
        alt={alt}
        className={`h-[180px] w-full object-cover small:aspect-[21/5] small:h-auto ${focus}`}
      />
      <h1
        className="absolute left-6 top-6 max-w-[60%] text-[28px] font-bold leading-tight md:left-10 md:top-8 md:text-[42px]"
        style={{ color }}
      >
        {children}
      </h1>
    </div>
  </PageSection>
);

/** Heading with a rule under it — the company pages' column headers. */
export const RuledHeading = ({ children, className = '' }) => (
  <div className={className}>
    <h2 className="text-[24px] font-bold text-black md:text-[30px]">{children}</h2>
    <div className="mt-3 h-px w-full bg-black/25" />
  </div>
);

// "/ You sure about that?" style section heading.
export const SectionHeading = ({ children, className = '' }) => (
  <h2 className={`text-[24px] font-bold leading-tight text-black md:text-[34px] ${className}`}>
    / {children}
  </h2>
);

/**
 * Outlined panel whose heading straddles the top border, like a fieldset
 * legend — used for the "/ 60+ Trendline" block.
 */
export const LegendBox = ({ legend, children }) => (
  <fieldset className="border border-black/40 px-4 pb-6 pt-2 md:px-8 md:pb-8">
    <legend className="px-3 text-[22px] font-bold text-black md:text-[30px]">/ {legend}</legend>
    {children}
  </fieldset>
);

/** Solid colour statistic plate: oversized figure over a short description. */
export const StatTile = ({ value, children, background, className = '' }) => (
  <div
    className={`flex flex-col justify-center px-4 py-4 text-white md:px-5 md:py-5 ${className}`}
    style={{ backgroundColor: background }}
  >
    <div className="text-[26px] font-bold leading-none md:text-[34px]">{value}</div>
    <p className="mt-2 text-[15px] leading-snug md:text-[17px]">{children}</p>
  </div>
);

/** Centred plate used inside the trendline box — figure and label stacked. */
export const CenteredStatTile = ({ lines, background }) => (
  <div
    className="flex flex-col items-center justify-center px-4 py-6 text-center text-white"
    style={{ backgroundColor: background }}
  >
    {lines.map((line) => (
      <div key={line} className="text-[16px] font-bold leading-snug md:text-[19px]">
        {line}
      </div>
    ))}
  </div>
);

export const SourceLine = ({ children }) => (
  <p className="text-[14px] text-black md:text-base">{children}</p>
);

/**
 * Closing brand lockup that ends every sub-page: the two-tone wordmark over
 * the product line.
 */
export const ClosingBrand = ({
  tagline = 'All the Scammer Prevention must have in one application.',
  taglineFirst = false,
  // False when the page has already set the wordmark elsewhere (Happens to
  // other people carries it over its closing image).
  showWordmark = true,
}) => {
  const wordmark = showWordmark ? (
    <p className="text-[56px] font-bold leading-none text-black md:text-[104px]">
      No / SCAM
    </p>
  ) : null;
  const line = tagline ? (
    <p className="mx-auto max-w-[30ch] text-[22px] font-bold leading-tight text-black md:text-[34px]">
      {tagline}
    </p>
  ) : null;
  // Hairline between the wordmark and the tagline (8-27 mockups). Sized to
  // sit a little wider than the wordmark, as drawn.
  const rule = tagline ? (
    <span
      aria-hidden="true"
      className="mx-auto my-5 block h-px w-full max-w-[420px] bg-black md:my-7 md:max-w-[900px]"
    />
  ) : null;

  return (
    <div className="px-4 py-10 text-center md:py-14">
      {taglineFirst ? line : wordmark}
      {rule}
      {taglineFirst ? wordmark : line}
    </div>
  );
};
